/**
 * NumberLine component for rendering number line visualizations
 * Displays a horizontal number line with tick marks, labels, and marked points
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

interface NumberLinePoint {
  value: number;
  label?: string;
  color?: string;
  marker?: 'dot' | 'arrow' | 'line';
}

interface NumberLineConfig {
  min: number;
  max: number;
  tickInterval?: number; // Interval between major ticks (e.g., 0.5, 1, 2)
  showMinorTicks?: boolean; // Show ticks between major intervals
  minorTickInterval?: number; // Interval for minor ticks
  points?: NumberLinePoint[]; // Points to mark on the line
  showLabels?: boolean; // Show number labels below ticks
  showZero?: boolean; // Always show zero label even if min > 0
  unit?: string; // Unit label (e.g., "sixths", "halves")
  title?: string;
  width?: number;
  height?: number;
}

interface NumberLineProps {
  config: NumberLineConfig;
}

const NumberLine: React.FC<NumberLineProps> = ({ config }) => {
  const {
    min,
    max,
    tickInterval = 1,
    showMinorTicks = false,
    minorTickInterval,
    points = [],
    showLabels = true,
    showZero = true,
    unit,
    title,
    width = 600,
    height = 120,
  } = config;

  const margin = { top: 20, right: 40, left: 40, bottom: 40 };
  const lineY = margin.top + 20;
  const lineLength = width - margin.left - margin.right;
  const range = max - min;
  const scale = lineLength / range;

  // Convert value to x position
  const valueToX = (value: number) => margin.left + (value - min) * scale;

  // Generate major ticks
  const majorTicks: number[] = [];
  const startTick = Math.ceil(min / tickInterval) * tickInterval;
  for (let tick = startTick; tick <= max; tick += tickInterval) {
    if (tick >= min && tick <= max) {
      majorTicks.push(tick);
    }
  }

  // Generate minor ticks if needed
  const minorTicks: number[] = [];
  if (showMinorTicks && minorTickInterval) {
    const minorStart = Math.ceil(min / minorTickInterval) * minorTickInterval;
    for (let tick = minorStart; tick <= max; tick += minorTickInterval) {
      if (tick >= min && tick <= max && !majorTicks.includes(tick)) {
        minorTicks.push(tick);
      }
    }
  }

  // Format number for display
  const formatNumber = (num: number): string => {
    // If it's a whole number, show without decimal
    if (Number.isInteger(num)) {
      return num.toString();
    }
    // For fractions, try to show as fraction if it's a common one
    const commonFractions: Record<number, string> = {
      0.125: '1/8',
      0.25: '1/4',
      0.333: '1/3',
      0.375: '3/8',
      0.5: '1/2',
      0.625: '5/8',
      0.667: '2/3',
      0.75: '3/4',
      0.875: '7/8',
    };
    const rounded = Math.round(num * 1000) / 1000;
    if (commonFractions[rounded]) {
      return commonFractions[rounded];
    }
    // Show as decimal with up to 3 decimal places
    return num.toFixed(3).replace(/\.?0+$/, '');
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      bgcolor: 'white',
      p: 2,
      borderRadius: 1,
    }}>
      {title && (
        <Typography 
          variant="h6" 
          sx={{ 
            color: '#2c3e50', 
            mb: 1,
            fontWeight: 600
          }}
        >
          {title}
        </Typography>
      )}
      <svg
        width={width}
        height={height}
        style={{
          border: '1px solid #e0e0e0',
          borderRadius: '5px',
          backgroundColor: 'white',
        }}
      >
        {/* Main number line */}
        <line
          x1={margin.left}
          y1={lineY}
          x2={width - margin.right}
          y2={lineY}
          stroke="#2c3e50"
          strokeWidth={3}
        />

        {/* Arrow at the end */}
        <polygon
          points={`${width - margin.right},${lineY} ${width - margin.right - 8},${lineY - 5} ${width - margin.right - 8},${lineY + 5}`}
          fill="#2c3e50"
        />

        {/* Major ticks */}
        {majorTicks.map((tick) => {
          const x = valueToX(tick);
          return (
            <g key={`major-${tick}`}>
              <line
                x1={x}
                y1={lineY - 8}
                x2={x}
                y2={lineY + 8}
                stroke="#2c3e50"
                strokeWidth={2}
              />
              {showLabels && (
                <text
                  x={x}
                  y={lineY + 28}
                  textAnchor="middle"
                  fontSize="14"
                  fill="#2c3e50"
                  fontWeight={600}
                >
                  {formatNumber(tick)}
                </text>
              )}
            </g>
          );
        })}

        {/* Minor ticks */}
        {minorTicks.map((tick) => {
          const x = valueToX(tick);
          return (
            <line
              key={`minor-${tick}`}
              x1={x}
              y1={lineY - 4}
              x2={x}
              y2={lineY + 4}
              stroke="#666"
              strokeWidth={1}
            />
          );
        })}

        {/* Zero marker (if needed) */}
        {showZero && min <= 0 && max >= 0 && !majorTicks.includes(0) && (
          <g>
            <line
              x1={valueToX(0)}
              y1={lineY - 8}
              x2={valueToX(0)}
              y2={lineY + 8}
              stroke="#2c3e50"
              strokeWidth={2}
            />
            {showLabels && (
              <text
                x={valueToX(0)}
                y={lineY + 28}
                textAnchor="middle"
                fontSize="14"
                fill="#2c3e50"
                fontWeight={600}
              >
                0
              </text>
            )}
          </g>
        )}

        {/* Marked points */}
        {points.map((point, index) => {
          const x = valueToX(point.value);
          const pointColor = point.color || '#9c27b0';
          const markerType = point.marker || 'dot';

          return (
            <g key={`point-${index}`}>
              {markerType === 'dot' && (
                <>
                  <circle
                    cx={x}
                    cy={lineY}
                    r={6}
                    fill={pointColor}
                    stroke="#fff"
                    strokeWidth={2}
                  />
                  {point.label && (
                    <text
                      x={x}
                      y={lineY - 15}
                      textAnchor="middle"
                      fontSize="14"
                      fontWeight="bold"
                      fill={pointColor}
                    >
                      {point.label}
                    </text>
                  )}
                </>
              )}
              {markerType === 'arrow' && (
                <>
                  <polygon
                    points={`${x},${lineY - 12} ${x - 6},${lineY - 6} ${x + 6},${lineY - 6}`}
                    fill={pointColor}
                  />
                  <line
                    x1={x}
                    y1={lineY - 6}
                    x2={x}
                    y2={lineY + 6}
                    stroke={pointColor}
                    strokeWidth={2}
                  />
                  {point.label && (
                    <text
                      x={x}
                      y={lineY - 20}
                      textAnchor="middle"
                      fontSize="14"
                      fontWeight="bold"
                      fill={pointColor}
                    >
                      {point.label}
                    </text>
                  )}
                </>
              )}
              {markerType === 'line' && (
                <>
                  <line
                    x1={x}
                    y1={lineY - 12}
                    x2={x}
                    y2={lineY + 12}
                    stroke={pointColor}
                    strokeWidth={3}
                  />
                  {point.label && (
                    <text
                      x={x}
                      y={lineY - 20}
                      textAnchor="middle"
                      fontSize="14"
                      fontWeight="bold"
                      fill={pointColor}
                    >
                      {point.label}
                    </text>
                  )}
                </>
              )}
            </g>
          );
        })}

        {/* Unit label */}
        {unit && (
          <text
            x={width - margin.right + 10}
            y={lineY}
            fontSize="12"
            fill="#666"
            dominantBaseline="middle"
          >
            {unit}
          </text>
        )}
      </svg>
    </Box>
  );
};

export default NumberLine;
